import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  fetchOrgSettings,
  VerticalCode,
  type OrgSettings,
  type VerticalCodeValue,
} from "../api/org";
import { useAuth } from "./AuthContext";

export interface OrgContextValue {
  orgId: string | null;
  orgName: string | null;
  baseCurrency: string | null;
  verticalCode: VerticalCodeValue;
  taxRegistrationNumber: string | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const OrgContext = createContext<OrgContextValue | null>(null);

function fallbackOrg(orgId: string): OrgSettings {
  return {
    org_id: orgId,
    org_name: orgId,
    base_currency: "EGP",
    vertical_code: VerticalCode.TRADING,
    tax_registration_number: null,
  };
}

export function OrgProvider({ children }: { children: ReactNode }) {
  const { profile, isAuthenticated } = useAuth();
  const [settings, setSettings] = useState<OrgSettings | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadOrg = useCallback(async () => {
    if (!profile?.orgId) {
      setSettings(null);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const org = await fetchOrgSettings();
      setSettings(org);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load organization";
      setError(message);
      setSettings(fallbackOrg(profile.orgId));
    } finally {
      setIsLoading(false);
    }
  }, [profile?.orgId]);

  useEffect(() => {
    if (!isAuthenticated || !profile?.orgId) {
      setSettings(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    void loadOrg();
  }, [isAuthenticated, profile?.orgId, loadOrg]);

  const value = useMemo<OrgContextValue>(
    () => ({
      orgId: settings?.org_id ?? profile?.orgId ?? null,
      orgName: settings?.org_name ?? null,
      baseCurrency: settings?.base_currency ?? null,
      verticalCode: settings?.vertical_code ?? VerticalCode.TRADING,
      taxRegistrationNumber: settings?.tax_registration_number ?? null,
      isLoading,
      error,
      refresh: loadOrg,
    }),
    [settings, profile?.orgId, isLoading, error, loadOrg],
  );

  return (
    <OrgContext.Provider value={value}>{children}</OrgContext.Provider>
  );
}

export function useOrg(): OrgContextValue {
  const ctx = useContext(OrgContext);
  if (!ctx) {
    throw new Error("useOrg must be used within OrgProvider");
  }
  return ctx;
}
